// src/components/CouponDialog.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../api/axios';
import classNames from 'classnames';

type CouponStatus = 'usable' | 'not_started' | 'expired' | 'inactive' | string;

export interface CouponItem {
  user_coupon_id: number;
  coupon_id: number;
  name?: string | null;
  code: string; // 表示は大文字
  link_code?: string | null;
  description?: string | null;

  discount_type?: 'percent' | 'amount' | 'fixed_price' | null;
  discount_value?: number | null;

  starts_at?: string | null; // ISO
  ends_at?: string | null;   // ISO
  is_active: boolean;
  status: CouponStatus;      // サーバー判定
  bound_at: string;          // ISO
  remaining_uses?: number | null; // 多张可用时的剩余次数
}

interface ValidateResp {
  ok: boolean;
  code?: string | null;
  link_code?: string | null;
  status?: CouponStatus | null;
  name?: string | null;
  discount_type?: 'percent' | 'amount' | 'fixed_price' | null;
  discount_value?: number | null;
  starts_at?: string | null;
  ends_at?: string | null;
  message?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** 选取模式：提供则启用“点击卡片即返回选择”的交互 */
  onPick?: (coupon: CouponItem) => void;
  /** 是否仅允许选择可用券（默认 true） */
  pickUsableOnly?: boolean;
}

// YYYY-MM-DD HH:mm
const fmtDateTime = (s?: string | null) =>
  s ? s.slice(0, 16).replace('T', ' ') : '—';

// 末尾4桁マスク（表示は大文字）
const maskCode = (raw: string) => {
  const up = (raw || '').toUpperCase();
  if (up.length <= 4) return '****';
  return up.slice(0, -4) + '****';
};

// 割引表示（返金という表現は使わない）
const renderBenefit = (c: CouponItem) => {
  if (!c.discount_type || c.discount_value == null) return null;
  if (c.discount_type === 'percent') return `割引: ${c.discount_value}%`;
  if (c.discount_type === 'amount') return `割引: ¥${c.discount_value}`;
  return `特別価格: ¥${c.discount_value}`; // fixed_price
};

// status → ラベル/色
const statusBadge = (status: CouponStatus) => {
  switch (status) {
    case 'usable':
      return { text: '有効', cls: 'bg-green-100 text-green-700' };
    case 'not_started':
      return { text: '未開始', cls: 'bg-coral-light text-coral' };
    case 'expired':
      return { text: '期限切れ', cls: 'bg-gray-200 text-gray-700' };
    case 'inactive':
      return { text: '無効', cls: 'bg-gray-200 text-gray-700' };
    default:
      // 例如后端传 'used' 之类，也归为「無効」
      return { text: '無効', cls: 'bg-gray-200 text-gray-700' };
  }
};

const CouponDialog: React.FC<Props> = ({
  open,
  onClose,
  onPick,
  pickUsableOnly = true,
}) => {
  const { t } = useTranslation();

  // 一览
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rows, setRows] = useState<CouponItem[]>([]);

  // 手输
  const [applyCode, setApplyCode] = useState('');
  const [applyBusy, setApplyBusy] = useState(false);
  const [applyMsg, setApplyMsg] = useState<string | null>(null);

  // 不可使用クーポン折叠开关
  const [showUnavailable, setShowUnavailable] = useState(false);

  const fetchCoupons = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await axios.get<CouponItem[]>('/user/coupons'); // 后端已过滤仅折扣券
      setRows(res.data || []);
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        t('common.error') ||
        'エラーが発生しました。';
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchCoupons();
    setShowUnavailable(false); // 打开弹窗时重置为折叠
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const usable = useMemo(
    () => rows.filter((r) => r.status === 'usable'),
    [rows],
  );
  const notUsable = useMemo(
    () => rows.filter((r) => r.status !== 'usable'),
    [rows],
  );

  // 点击卡片选择
  const handleChoose = (c: CouponItem) => {
    if (onPick) {
      if (pickUsableOnly && c.status !== 'usable') return; // 禁止选择不可用券
      onPick(c);
      onClose();
    }
  };

  // 手输并绑定（成功后触发 onPick）
  const onApply = async () => {
    const raw = (applyCode || '').trim();
    if (!raw) {
      setApplyMsg(
        t('auth.error_empty') || 'メールアドレスとパスワードを入力してください',
      );
      return;
    }
    setApplyBusy(true);
    setApplyMsg(null);
    try {
      const codeUpper = raw.toUpperCase();
      // 1) 验证
      const vres = await axios.post<ValidateResp>('/user/coupons/validate', {
        code: codeUpper,
      });
      const v = vres.data;
      if (!v.ok) {
        let msg: string | null = null;

        if (v.message === 'Coupon not found') {
          msg = t('coupons.not_found');
        } else {
          msg =
            v.message ||
            t('coupons.validate_failed') ||
            'このコードは割引に使用できません。';
        }

        setApplyMsg(msg);
        return;
      }

      if (!v.discount_type || v.discount_value == null) {
        const msg =
          v.message ||
          t('coupons.referral_only') ||
          '紹介リンク専用のコードです（割引は適用されません）。';
        setApplyMsg(String(msg));
        return;
      }
      // 2) 绑定（幂等）
      const bind = await axios.post<CouponItem>('/user/coupons/bind', {
        code: codeUpper,
      });
      const bound = bind.data;
      // 3) 刷新 & 选择后关闭
      await fetchCoupons();
      setApplyMsg(
        t('mypage.coupon_bound_success') || 'クーポンを適用しました',
      );
      setApplyCode('');
      if (onPick) {
        onPick(bound);
        onClose();
      }
    } catch (e: any) {
      // 这里做错误文案映射，避免直接显示英文 "Coupon is inactive"
      const detail: string =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        '';

      let msg: string;

      if (/inactive/i.test(detail) || /disabled/i.test(detail)) {
        // 对应后端 "Coupon is inactive"
        msg =
          t('coupons.not_usable') ||
          t('coupons.validate_failed') ||
          (t('common.error') as string) ||
          'このクーポンは現在ご利用いただけません。';
      } else if (/expired/i.test(detail)) {
        msg =
          t('coupons.expired') ||
          t('coupons.not_usable') ||
          (t('common.error') as string) ||
          'このクーポンは有効期限が切れています。';
      } else if (/not found/i.test(detail)) {
        msg =
          t('coupons.validate_failed') ||
          (t('common.error') as string) ||
          'このコードは割引に使用できません。';
      } else {
        msg =
          (t('common.error') as string) ||
          'エラーが発生しました。';
      }

      setApplyMsg(msg);
    } finally {
      setApplyBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-lg p-6">
        <h2 className="text-lg font-semibold mb-4">
          {t('mypage.view_coupons') || 'クーポン'}
        </h2>

        {/* 手入力 → 検証＋バインド（成功后触发 onPick 并关闭） */}
        <div className="mb-5">
          <label className="block text-sm text-gray-700 mb-1">
            {t('auth.coupon_label') || 'クーポンコード'}
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={applyCode}
              onChange={(e) => setApplyCode(e.target.value)}
              onCompositionEnd={(e) =>
                setApplyCode(e.currentTarget.value.toUpperCase())
              }
              onBlur={(e) =>
                setApplyCode(e.target.value.toUpperCase())
              }
              placeholder={
                t('auth.coupon_placeholder') ||
                'お持ちの方はご入力ください'
              }
              className="flex-1 border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-coral"
              autoCapitalize="none"
              autoComplete="off"
              spellCheck={false}
              autoCorrect="off"
            />
            <button
              onClick={onApply}
              disabled={applyBusy || !applyCode.trim()}
              className={classNames(
                'px-4 py-2 rounded-lg text-white',
                applyBusy || !applyCode.trim()
                  ? 'bg-coral/80 opacity-60 cursor-not-allowed'
                  : 'bg-coral hover:opacity-90',
              )}
            >
              {applyBusy
                ? t('auth.sending') || '送信中…'
                : t('common.apply') || '適用'}
            </button>
          </div>
          {applyMsg && (
            <p className="text-sm mt-2 text-gray-700">{applyMsg}</p>
          )}
        </div>

        {/* 一覧（卡片可点击选择） */}
        {loading ? (
          <p className="text-sm text-gray-600">
            {t('common.loading') || '読み込み中...'}
          </p>
        ) : err ? (
          <p className="text-sm text-red-600">{err}</p>
        ) : usable.length === 0 && notUsable.length === 0 ? (
          <p className="text-sm text-gray-600">
            {t('coupons.list.empty') || 'クーポンがありません'}
          </p>
        ) : (
          <div className="space-y-6">
            {/* 使用可能クーポン */}
            {usable.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold text-green-700 mb-2">
                  {t('coupons.section_usable') || '使用可能'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {usable.map((c) => {
                    const badge = statusBadge(c.status);
                    const count =
                      c.remaining_uses != null && c.remaining_uses > 0
                        ? c.remaining_uses
                        : 1;

                    return Array.from({ length: count }).map((_, idx) => (
                      <button
                        type="button"
                        key={`${c.user_coupon_id}-${idx}`}
                        onClick={() => handleChoose(c)}
                        className="text-left border rounded-xl p-4 hover:shadow transition"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="text-sm text-gray-600">
                              {c.name || 'クーポン'}
                            </div>
                            <div className="font-mono text-base">
                              {maskCode(c.code)}
                            </div>
                          </div>
                          <span
                            className={classNames(
                              'inline-flex items-center px-2 py-0.5 rounded text-xs',
                              badge.cls,
                            )}
                          >
                            {badge.text}
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-gray-700">
                          {c.discount_type && c.discount_value != null && (
                            <div>{renderBenefit(c)}</div>
                          )}
                          <div className="mt-1">
                            期間: {fmtDateTime(c.starts_at)} ~{' '}
                            {fmtDateTime(c.ends_at)}
                          </div>
                        </div>
                      </button>
                    ));
                  })}
                </div>
              </section>
            )}

            {/* 不可使用クーポン：默认折叠 */}
            {notUsable.length > 0 && (
              <section>
                <button
                  type="button"
                  onClick={() =>
                    setShowUnavailable((v) => !v)
                  }
                  className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 mb-2 px-2 py-1 rounded hover:bg-gray-50"
                >
                  <span>
                    {t('coupons.section_unavailable') ||
                      '期限切れ / 未開始 / 無効'}
                    （{notUsable.length}）
                  </span>
                  <span className="text-xs text-gray-500">
                    {showUnavailable
                      ? t('common.hide') || '隠す'
                      : t('common.show') || '表示'}
                  </span>
                </button>

                {showUnavailable && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {notUsable.map((c) => {
                      const badge = statusBadge(c.status);
                      return (
                        <div
                          key={c.user_coupon_id}
                          className="border rounded-xl p-4 opacity-80 bg-gray-50"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="text-sm text-gray-600">
                                {c.name || 'クーポン'}
                              </div>
                              <div className="font-mono text-base">
                                {maskCode(c.code)}
                              </div>
                            </div>
                            <span
                              className={classNames(
                                'inline-flex items-center px-2 py-0.5 rounded text-xs',
                                badge.cls,
                              )}
                            >
                              {badge.text}
                            </span>
                          </div>
                          <div className="mt-2 text-xs text-gray-700">
                            {c.discount_type &&
                              c.discount_value != null && (
                                <div>{renderBenefit(c)}</div>
                              )}
                            <div className="mt-1">
                              期間: {fmtDateTime(c.starts_at)} ~{' '}
                              {fmtDateTime(c.ends_at)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-100"
          >
            {t('common.close') || '閉じる'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CouponDialog;
